import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getGroupLedgerReport } from '../../../../redux/actions/SocietyPortal/AccountReportsAction/AccountReportsAction';
import { getChartOfAccountsDropdown } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import ReportShell, { ReportActionButtons } from '../components/ReportShell';
import { FinancialYearField, DateField, GetReportButton } from '../components/ReportFilters';
import { getCurrentFY, fyDateRange } from '../components/helpers';
import AccountLedger from './AccountLedger';

const labelClass = 'tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1';

const GroupLedger = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { glData, glLoading } = useSelector(state => state.AccountReportsReducer || {});
    const { coaDropdown, coaDropdownLoading } = useSelector(state => state.JournalReducer || {});

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [groupCode, setGroupCode] = useState('');
    const [fromDate, setFromDate] = useState(fyDateRange(getCurrentFY()).from);
    const [toDate, setToDate] = useState(fyDateRange(getCurrentFY()).to);

    useEffect(() => {
        if (societyId) {
            dispatch(getChartOfAccountsDropdown({ societyId }));
        }
    }, [societyId, dispatch]);

    useEffect(() => {
        const range = fyDateRange(financialYear);
        setFromDate(range.from);
        setToDate(range.to);
    }, [financialYear]);

    // Default to first group when dropdown loads
    useEffect(() => {
        if (!groupCode && coaDropdown && coaDropdown.length > 0) {
            // Prefer parent groups (codes with hyphens like "01-01")
            const firstGroup = coaDropdown.find((acc) => acc.ac_code && acc.ac_code.split('-').length <= 2) || coaDropdown[0];
            if (firstGroup) setGroupCode(firstGroup.ac_code);
        }
    }, [coaDropdown, groupCode]);

    const groupOptions = useMemo(() => {
        return (coaDropdown || []).map((acc) => ({
            value: acc.ac_code,
            label: `${acc.name} (${acc.ac_code})`,
        }));
    }, [coaDropdown]);

    const handleGet = () => {
        if (!societyId || !groupCode) return;
        dispatch(getGroupLedgerReport({
            societyId,
            groupAccountCode: groupCode,
            fromDate,
            toDate,
        }));
    };
    usePageRefresh(handleGet);

    return (
        <ReportShell
            title="Group Ledger"
            actions={<ReportActionButtons onExcel={() => window.print()} onPrint={() => window.print()} />}
            filters={(
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-3 tw-items-end">
                    <FinancialYearField value={financialYear} onChange={setFinancialYear} />
                    <div>
                        <label className={labelClass}>Group Account</label>
                        <SearchableDropdown
                            value={groupCode || 'all'}
                            onChange={(v) => setGroupCode(v === 'all' ? '' : v)}
                            options={groupOptions}
                            placeholder="Select group"
                            loading={coaDropdownLoading}
                            clearable={false}
                            small
                        />
                    </div>
                    <DateField label="From Date" value={fromDate} onChange={setFromDate} />
                    <DateField label="To Date" value={toDate} onChange={setToDate} />
                    <GetReportButton onClick={handleGet} loading={glLoading} />
                </div>
            )}
        >
            {glLoading && !glData ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                        <span className="tw-text-xs tw-text-gray-400">Loading group ledger...</span>
                    </div>
                </div>
            ) : glData ? (
                <div className="tw-p-4 tw-bg-gray-50/30">
                    {glData.reportInfo && (
                        <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:folder-outline" className="tw-text-rose-500 tw-text-sm" />
                            <span className="tw-text-xs tw-font-semibold tw-text-gray-700">{glData.reportInfo.groupAccountName}</span>
                            <span className="tw-text-[11px] tw-text-gray-400 tw-font-mono">({glData.reportInfo.groupAccountCode})</span>
                        </div>
                    )}
                    {(glData.ledgers || []).length > 0 ? (
                        glData.ledgers.map((l) => <AccountLedger key={l.accountId ?? l.accountCode} ledger={l} />)
                    ) : (
                        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-py-12 tw-text-center tw-text-xs tw-text-gray-400">
                            No accounts found in this group
                        </div>
                    )}
                </div>
            ) : (
                <div className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                    Select a group account and click <strong className="tw-text-gray-600">Get Report</strong>
                </div>
            )}
        </ReportShell>
    );
};

export default GroupLedger;
